import { useEffect, useRef } from 'react'
import type { RefObject } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'

/** Number of bars in the waveform — the design's 6-bar glyph drawn twice. */
const BAR_COUNT = 12

/** Geometry of the SVG canvas (px). Bars are vertical lines spaced one STEP
 *  apart, with the first half-step of margin on each side so the row is centred.
 *  Drawing into an SVG keeps the spacing mathematically exact — flexbox could
 *  round thin bars to uneven sub-pixel gaps. */
const STEP = 3
const STROKE = 1.6
const HEIGHT = 20
const WIDTH = BAR_COUNT * STEP
const CENTER_Y = HEIGHT / 2
/** Longest a bar can grow while keeping its round caps inside the canvas. */
const FULL_H = HEIGHT - STROKE

/** Resting height every bar holds in silence — the Figma "silent voice yet"
 *  glyph draws all bars as equal short stubs (2.75 of its 22u box ≈ 0.125).
 *  Speech swells bars above this; silence settles them back to the flat row. */
const IDLE_LEVEL = 0.125

/** Skip the lowest FFT bins (DC / mains hum) before mapping bars to bands. */
const LOW_BIN = 2
/** How many bins above {@link LOW_BIN} the bars span — voice lives in the lows. */
const USABLE_BINS = 44
/** Gain on each band so normal speech drives the bars to full height. */
const BAND_GAIN = 1.7
/** Per-bar easing: rise fast on sound, settle slowly back (a VU-meter feel). */
const ATTACK = 0.5
const RELEASE = 0.12

type BarRefs = RefObject<(SVGLineElement | null)[]>

function createAudioContext(): AudioContext {
  const w = window as unknown as {
    AudioContext?: typeof AudioContext
    webkitAudioContext?: typeof AudioContext
  }
  const Ctx = w.AudioContext ?? w.webkitAudioContext
  if (!Ctx) throw new Error('Web Audio API is not supported')
  return new Ctx()
}

/** X centre of bar `i` — half a step of margin, then one step per bar. */
function barX(index: number): number {
  return STEP / 2 + index * STEP
}

/** Write a 0…1 level to a bar by growing it symmetrically about the centre line,
 *  changing only its height — never its x position or width. */
function drawBar(line: SVGLineElement | null, level: number): void {
  if (!line) return
  const half = (Math.min(1, Math.max(0, level)) * FULL_H) / 2
  line.setAttribute('y1', String(CENTER_Y - half))
  line.setAttribute('y2', String(CENTER_Y + half))
}

/** Map a bar to a frequency bin by its distance from centre: the middle bars
 *  read the low (loud) end of the spectrum and the edges the high end, so a
 *  voice naturally pushes the wave tall in the centre and short at the sides. */
function bandForBar(index: number): number {
  const centre = (BAR_COUNT - 1) / 2
  const distance = Math.abs(index - centre) / centre // 0 (centre) … 1 (edge)
  return LOW_BIN + Math.round(distance * (USABLE_BINS - 1))
}

/** Drive the bar heights from the live microphone spectrum. While `stream` is
 *  set, an AnalyserNode feeds a rAF loop that writes each bar's height straight
 *  to the DOM — no React state per frame. Each bar tracks its own frequency band
 *  with attack/release smoothing and never falls below the idle silhouette, so
 *  silence rests on the design's wave shape and speech swells above it. */
function useAudioBars(stream: MediaStream | null, barsRef: BarRefs) {
  useEffect(() => {
    if (!stream) return
    let ctx: AudioContext
    try {
      ctx = createAudioContext()
    } catch {
      return
    }
    const analyser = ctx.createAnalyser()
    analyser.fftSize = 512
    // Built-in spectral smoothing, layered under our per-bar attack/release.
    analyser.smoothingTimeConstant = 0.7
    ctx.createMediaStreamSource(stream).connect(analyser)

    const spectrum = new Uint8Array(analyser.frequencyBinCount)
    const bands = Array.from({ length: BAR_COUNT }, (_, i) => bandForBar(i))
    const levels = new Array<number>(BAR_COUNT).fill(0)
    let raf = 0

    const tick = () => {
      analyser.getByteFrequencyData(spectrum)
      for (let i = 0; i < BAR_COUNT; i++) {
        const target = Math.min(1, (spectrum[bands[i]] / 255) * BAND_GAIN)
        // Rise quickly toward louder targets, fall back gently to the baseline.
        const ease = target > levels[i] ? ATTACK : RELEASE
        levels[i] += (target - levels[i]) * ease
        // Never drop below the resting stub height, so a quiet mic reads as the
        // design's flat silent row rather than vanishing.
        drawBar(barsRef.current[i], Math.max(IDLE_LEVEL, levels[i]))
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(raf)
      void ctx.close()
      // Settle back to the flat silent row for the next recording.
      for (let i = 0; i < BAR_COUNT; i++) {
        drawBar(barsRef.current[i], IDLE_LEVEL)
      }
    }
  }, [stream, barsRef])
}

/** Animated equalizer drawn on the dark recording island as thin round-capped
 *  white lines, fed by real mic amplitude via {@link useAudioBars}. */
function VoiceWaveform({ stream }: { stream: MediaStream | null }) {
  const barsRef = useRef<(SVGLineElement | null)[]>([])
  useAudioBars(stream, barsRef)
  return (
    <svg
      className="h-5"
      width={WIDTH}
      height={HEIGHT}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      fill="none"
      aria-hidden
    >
      {Array.from({ length: BAR_COUNT }, (_, i) => {
        const half = (IDLE_LEVEL * FULL_H) / 2
        return (
          <line
            key={i}
            ref={(el) => {
              barsRef.current[i] = el
            }}
            x1={barX(i)}
            x2={barX(i)}
            y1={CENTER_Y - half}
            y2={CENTER_Y + half}
            stroke="currentColor"
            strokeWidth={STROKE}
            strokeLinecap="round"
          />
        )
      })}
    </svg>
  )
}

/** Dark pill shown in place of the mic while recording; tapping it stops.
 *  Sized by its wrapper so it can morph in/out smoothly. */
export function VoiceRecordingIsland({
  onStop,
  stream,
  className,
}: {
  onStop: () => void
  stream: MediaStream | null
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={onStop}
      aria-label={t('voice.stop')}
      className={cn(
        'flex h-9 cursor-pointer items-center justify-center rounded-full bg-gray-900 px-3 text-white transition-[filter] duration-200 hover:brightness-125',
        className,
      )}
    >
      <VoiceWaveform stream={stream} />
    </button>
  )
}
